// Partner link (Settings, Partner): what the PWA shows and the one switch for everything
// split-related. Splits stay hidden unless the household has an active partner link.
import type { PartnerDto } from './api';

/** How long an invite code works. */
export const PARTNER_INVITE_HOURS = 48;
/** How long an emailed invite link works. */
export const PARTNER_INVITE_DAYS = 7;

/** "Tiffany joined and is linked": shown to the inviter after an email invite was used. */
export const joinedLine = (name: string) => `${name} joined and is linked`;

/** The emailed invite: plain text, no amounts, a link to the app only. */
export const partnerInviteEmail = (from: string, link: string) => ({
  subject: `${from} invited you to Hush`,
  text: `${from} invited you to Hush, a budget that runs itself, and to link your budgets so you can split shared costs. You each keep your own budget; neither of you sees the other's purchases.

Open this link on your phone to sign up with this email address. It works once, for ${PARTNER_INVITE_DAYS} days:

${link}

Already use Hush? Sign in with this email first, then open the link again to link with ${from}.
If you did not expect this, you can ignore it.`,
});

/** The sign-up code field: a partner link code (XXXX-XXXX) pasted there by mistake. */
export const LOOKS_LIKE_PARTNER_CODE = 'That looks like a partner link code. Sign up first, then enter it under Settings, Partner.';
export const looksLikePartnerCode = (code: string) => /^[A-Za-z0-9]{4}[\s-]?[A-Za-z0-9]{4}$/.test(code.trim());

/** The same answer for every invite link that cannot be used with the email given. */
export const INVITE_NOT_VALID = 'This invite link does not work with that email, or it has expired or was used. Ask for a new invite.';
/** The invite matched, but that email already has a Hush account: sign in, then open the link again. */
export const INVITE_EXISTING_ACCOUNT = 'You already have a Hush account with this email. Sign in with email, then open this invite link again to link.';

/** Split requests (a later PR) are on only while the household has an active partner link. */
export const splitsEnabled = (p: PartnerDto | null | undefined): boolean => !!p?.linked;

/** The Splits screen: with a link, or (view only) while a balance is left after an unlink. */
export const settleVisible = (p: PartnerDto | null | undefined): boolean => !!p?.linked || !!p?.balanceLeft;

/** "Partner: Tiffany (linked)" */
export const partnerLine = (name: string) => `Partner: ${name} (linked)`;

export const PARTNER_INTRO = "Link with a partner who has their own Hush budget. Your budgets, categories and cards stay separate, and neither of you sees the other's purchases.";
export const PARTNER_CODE_HINT = (hours: number) => `Give this code to your partner. They enter it in Settings, Partner, on their phone. It works once, for ${hours} hours.`;
export const PARTNER_UNLINK_HINT = 'Your budgets stay separate. Unlinking does not change your own budget.';
